import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';

// ----------------------------------------------------------------------

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const { lang } = useParams();
  const location = useLocation();

  const handleLanguageMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = useCallback(
    async (newLang) => {
      try {
        // Get the current path without the language prefix
        const pathWithoutLang = location.pathname.replace(`/${lang}`, '');

        // Change the language
        await i18n.changeLanguage(newLang);

        // Store the language in localStorage for persistence
        localStorage.setItem('i18nextLng', newLang);

        // Navigate to the same path with the new language
        navigate(`/${newLang}${pathWithoutLang}`);
        handleLanguageClose();
      } catch (error) {
        console.error('Error changing language:', error);
      }
    },
    [lang, location.pathname, i18n, navigate]
  );

  // Sync language when URL lang param changes
  useEffect(() => {
    const syncLanguage = async () => {
      if (lang && i18n.isInitialized && lang !== i18n.language) {
        try {
          await i18n.changeLanguage(lang);
          localStorage.setItem('i18nextLng', lang);
        } catch (error) {
          console.error('Error syncing language:', error);
        }
      }
    };

    syncLanguage();
  }, [lang, i18n]);

  // Use URL lang as primary source, fallback to i18n language
  const currentLanguage = lang || i18n.language || 'uz';

  return (
    <>
      <Button
        onClick={handleLanguageMenu}
        sx={{
          minWidth: 'auto',
          p: 1,
          color: 'text.primary',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Box
          component="img"
          src={`/assets/icons/flags/${currentLanguage}.svg`}
          alt={currentLanguage}
          sx={{
            width: 24,
            height: 24,
            borderRadius: 0.5,
          }}
        />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleLanguageClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 120,
          },
        }}
      >
        <MenuItem
          onClick={() => handleLanguageChange('uz')}
          selected={currentLanguage === 'uz'}
          sx={{
            py: 1,
            px: 2,
          }}
        >
          <Box
            component="img"
            src="/assets/icons/flags/uz.svg"
            alt="uz"
            sx={{
              width: 24,
              height: 24,
              mr: 1,
              borderRadius: 0.5,
            }}
          />
          {t('language.uzbek')}
        </MenuItem>

        <MenuItem
          onClick={() => handleLanguageChange('ru')}
          selected={currentLanguage === 'ru'}
          sx={{
            py: 1,
            px: 2,
          }}
        >
          <Box
            component="img"
            src="/assets/icons/flags/ru.svg"
            alt="ru"
            sx={{
              width: 24,
              height: 24,
              mr: 1,
              borderRadius: 0.5,
            }}
          />
          {t('language.russian')}
        </MenuItem>
      </Menu>
    </>
  );
}
